import { useCallback } from 'react';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types';
import { usePagedList } from '../ui/use-paged-list';

export type TenantRow = components['schemas']['TenantRowResponse'];
export type TenantRowPage = components['schemas']['TenantRowPageResponse'];

/**
 * Tenant's rows (FR-AD-6), at the platform: every tenant with its organizations and its people,
 * counted and never listed (FR-GA-5). A platform route is evaluated in the caller's own active
 * organization, so it carries its headers, as the place chooser's does; without one the read
 * answers as the server would (ACTIVE_CONTEXT_REQUIRED).
 */
export function useTenants(q: string) {
  const active = useActiveContextOptional()?.active ?? null;
  const tenantId = active?.tenantId;
  const organizationId = active?.organizationId;
  const read = useCallback(
    (cursor: string | null) =>
      tenantId === undefined || organizationId === undefined
        ? Promise.resolve({
            error: { code: 'ACTIVE_CONTEXT_REQUIRED' },
            response: new Response(null, { status: 400 }),
          })
        : apiClient.GET('/v1/administration/platform/tenant-overview', {
            params: {
              query: { ...(q !== '' ? { q } : {}), ...(cursor !== null ? { cursor } : {}) },
              header: {
                'X-Active-Tenant-Id': tenantId,
                'X-Active-Organization-Id': organizationId,
              },
            },
          }),
    [tenantId, organizationId, q],
  );
  return usePagedList<TenantRow, TenantRowPage>(read);
}

/** The headers a platform write carries, or null when there is no active organization. */
export function usePlatformHeaders(): {
  readonly 'X-Active-Tenant-Id': string;
  readonly 'X-Active-Organization-Id': string;
} | null {
  const active = useActiveContextOptional()?.active ?? null;
  return active === null
    ? null
    : { 'X-Active-Tenant-Id': active.tenantId, 'X-Active-Organization-Id': active.organizationId };
}
